import { useEffect, useRef, useState, type ReactNode } from 'react';

import { controlExclusionZones, type MapRect } from '@/lib/maps/control-zones';
import { placeMapPanel, type PanelSide } from '@/lib/maps/panel-placement';

import styles from './map-anchored-panel.module.scss';

export type MapAnchoredPanelProps = {
  /** The projected box of the thing the panel describes: a Field's Boundary bounds, later a marker's pin. */
  anchor: MapRect;
  /** The panel's nominal size — the input the placement arithmetic needs before layout. */
  size: { width: number; height: number };
  /** The map area's own size in CSS pixels. */
  viewport: { width: number; height: number };
  /** The dialog's accessible name. */
  label: string;
  /**
   * Hold keyboard focus inside the panel (113588 §13c, §13d).
   *
   * Off by default, and that default is load-bearing: 113586 §4f wants the top navigation, legend
   * and map controls keyboard-reachable while the **Field** panel is open, and a trap and that
   * cannot both hold. The Device quick view opts in because §13d-i decides the other way for it, and
   * pays for it with Escape as a hard requirement rather than a convenience (§3b) — it is the only
   * way out.
   */
  trapFocus?: boolean;
  /**
   * Choose the side once, at open and on each resize, and keep it as the map moves (113588 §2c vs
   * §2f). Off by default: 113586 has no equivalent rule, so the Field panel keeps re-placing freely.
   */
  lockSide?: boolean;
  /** Chrome. `padded` is the Field panel's card; `flush` drops the padding and takes the quick
   *  view's 6px radius, so its header rule can span the full card width. */
  chrome?: 'padded' | 'flush';
  children: ReactNode;
};

/**
 * A details panel anchored **to the map**, not to the screen (113586 §4a–§4d; grilling Q2, locked
 * decision 2). One shell with the body injected, so the criteria the Field panel and ticket 11's
 * Device quick view state identically are one implementation and one test suite.
 *
 * **Where it goes is not decided here.** `placeMapPanel` owns beside-not-over, the edge flip, the
 * control exclusion zones and the oversized-Field resolution; this component's whole job is to hand
 * it the four inputs and apply the answer. That is what makes §4c and §4f arithmetic in the `node`
 * project rather than untestable layout.
 *
 * **Travel is free.** It is an absolutely-positioned sibling of the canvas inside `.mapArea`, and the
 * caller re-projects the anchor on every camera frame — so the panel follows its Field on pan and
 * zoom (§4d) with no camera subscription, and repositions on a window resize for the same reason. A
 * `<MapOverlay>` portal would buy nothing: `placeMapPanel` already answers in this element's own
 * coordinate space.
 *
 * `role="dialog"` **without** `aria-modal` — for the Field panel; the Device quick view opts into
 * `trapFocus` per 113588 §13d-i — and focus merely *moves* here on open, it does not trap, unless
 * the caller asks for it.
 */
export function MapAnchoredPanel({
  anchor,
  size,
  viewport,
  label,
  trapFocus = false,
  lockSide = false,
  chrome = 'padded',
  children,
}: MapAnchoredPanelProps) {
  const ref = useRef<HTMLDivElement>(null);

  // The side chosen at open, kept across camera frames (§2f) and thrown away when the map area
  // itself resizes (§16b). Plain `useState`, updated conditionally during render rather than a ref
  // or an effect — the "adjusting state based on a prop change" pattern React documents for exactly
  // this shape: derived from this render's own placement, and self-correcting in one extra render
  // pass rather than a `useEffect` that would commit the wrong side for a frame first.
  const [lockedSide, setLockedSide] = useState<PanelSide | null>(null);
  const viewportKey = `${viewport.width}x${viewport.height}`;
  const [measuredViewportKey, setMeasuredViewportKey] = useState(viewportKey);
  if (measuredViewportKey !== viewportKey) {
    setMeasuredViewportKey(viewportKey);
    setLockedSide(null);
  }

  const placement = placeMapPanel({
    anchor,
    panel: size,
    viewport,
    exclusions: controlExclusionZones(viewport),
    ...(lockSide && lockedSide ? { side: lockedSide } : {}),
  });
  if (lockSide && lockedSide !== placement.side) {
    setLockedSide(placement.side);
  }

  // Focus moves in when the panel opens so the keyboard reaches its controls without tabbing the
  // whole map, and ONCE only — re-focusing on every camera frame would fight the Grower.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  // §13d. Scoped to this element rather than the document, so the trap dies with the panel and
  // cannot outlive it — the failure mode that leaves a page unusable after a close.
  useEffect(() => {
    const element = ref.current;
    if (!trapFocus || !element) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const stops = [
        ...element.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const first = stops[0];
      const last = stops[stops.length - 1];
      // §3a leaves this panel a single control, so "past the last element" and "before the first"
      // are usually the same stop — the guard below still reads correctly if one is ever added.
      if (!first || !last) {
        event.preventDefault();
        element.focus();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === element)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    element.addEventListener('keydown', onKeyDown);
    return () => element.removeEventListener('keydown', onKeyDown);
  }, [trapFocus]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={`${styles.panel} ${styles[chrome]}`}
      style={{ left: `${placement.x}px`, top: `${placement.y}px`, width: `${size.width}px` }}
    >
      {children}
    </div>
  );
}
